<template>
  <v-container>
    <v-row>
      <v-col>
        <div class="d-flex justify-center mb-2 mt-2">
          <v-text-field
            label="Search"
            outlined
            dense
            class="ml-5"
            v-model="reviewerSearchBox"
            disabled
          >
          </v-text-field>
          <v-btn color="primary" class="ml-2 mr-5" disabled> Search </v-btn>
        </div>
        <p v-if="reviewerSearch.length > 0">
          Searching for: "{{ reviewerSearch }}"
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-expansion-panels>
          <v-expansion-panel v-for="(item, i) in reviewers" :key="i">
            <v-expansion-panel-header>
              <div class="d-flex align-center flex-wrap" style="gap: 12px">
                <v-chip
                  small
                  :color="item.Status === 'active' ? 'green' : 'orange'"
                  text-color="white"
                >
                  {{ item.Status }}
                </v-chip>
                <span>{{ item.Email }}</span>
                <v-spacer></v-spacer>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" class="grey--text text-body-2">
                      {{ $utils.relativeTimeFromTs(item.DateCreated) }}
                    </span>
                  </template>
                  <span>{{ $utils.timeFromTs(item.DateCreated) }}</span>
                </v-tooltip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col cols="12" sm="6">
                  <strong>Public name:</strong>
                  {{ item.PublicName || "—" }}
                </v-col>
                <v-col cols="12" sm="6">
                  <strong>LinkedIn:</strong>
                  <a
                    v-if="item.LinkedinUrl"
                    :href="item.LinkedinUrl"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ item.LinkedinUrl }}
                  </a>
                  <span v-else>—</span>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-chip
                    small
                    :color="item.NeedsVerification ? 'orange' : 'green'"
                    text-color="white"
                  >
                    {{
                      item.NeedsVerification
                        ? "Needs verification"
                        : "Verified"
                    }}
                  </v-chip>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-chip
                    small
                    :color="item.PaymentSetupValid ? 'green' : 'red'"
                    text-color="white"
                  >
                    {{
                      item.PaymentSetupValid
                        ? "Payment setup valid"
                        : "Payment setup missing"
                    }}
                  </v-chip>
                </v-col>
                <v-col
                  cols="12"
                  v-if="item.Companies && item.Companies.length > 0"
                >
                  <strong>Companies:</strong>
                  <v-chip
                    small
                    v-for="c in item.Companies"
                    :key="c"
                    class="ml-1 mb-1"
                  >
                    {{ c }}
                  </v-chip>
                </v-col>
                <v-col cols="12">
                  <strong>Experience:</strong>
                  <div
                    v-if="parseExperience(item).length === 0"
                    class="grey--text"
                  >
                    None listed
                  </div>
                  <v-card
                    v-for="exp in parseExperience(item)"
                    :key="exp.id"
                    outlined
                    class="mt-2 pa-3"
                  >
                    <div class="d-flex align-center flex-wrap" style="gap: 8px">
                      <v-chip small v-if="exp.selCompany && exp.selCompany.Name">
                        {{ exp.selCompany.Name }}
                      </v-chip>
                      <span v-else class="font-italic">
                        {{ exp.manualCompanyName || "Unknown company" }}
                      </span>
                      <span class="grey--text">
                        ({{ exp.startDate }} -
                        {{ exp.isCurrent ? "now" : exp.endDate }})
                      </span>
                      <v-chip
                        v-if="exp.isVerified"
                        small
                        color="green"
                        text-color="white"
                      >
                        verified
                      </v-chip>
                    </div>
                    <div
                      class="mt-1"
                      v-if="exp.selRoles && exp.selRoles.length > 0"
                    >
                      {{ exp.selRoles.join(", ") }}
                    </div>
                  </v-card>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="d-flex align-center">
                  <v-alert
                    v-if="verifyErrors[item.Id]"
                    type="error"
                    dense
                    outlined
                    class="mb-0 mr-3 py-1"
                  >
                    {{ verifyErrors[item.Id] }}
                  </v-alert>
                  <v-spacer></v-spacer>
                  <v-btn
                    :color="item.NeedsVerification ? 'green' : 'orange'"
                    :loading="!!verifyLoading[item.Id]"
                    @click="toggleVerification(item)"
                  >
                    {{ item.NeedsVerification ? "Verify" : "Un-verify" }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
      <v-col>
        <div class="d-flex">
          <v-spacer></v-spacer>
          <v-btn plain v-if="reviewersLoaded" disabled> Load more </v-btn>
          <v-spacer></v-spacer>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="!reviewersLoaded">
      <v-col>
        <div class="d-flex justify-center">
          <v-progress-circular
            class="mt-10 mb-10"
            :size="50"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      reviewers: [],
      reviewersLoaded: false,
      reviewersLastEmail: "",
      reviewerSearch: "",
      reviewerSearchBox: "",
      btnReviewersNextDisabled: true,
      verifyLoading: {},
      verifyErrors: {},
    };
  },
  methods: {
    async toggleVerification(item) {
      var newValue = !item.NeedsVerification;
      var confirmMsg = newValue
        ? `Mark ${item.Email} as un-verified? They will be sent back to the verification queue.`
        : `Mark ${item.Email} as verified?`;
      if (!confirm(confirmMsg)) {
        return;
      }
      this.$set(this.verifyErrors, item.Id, "");
      this.$set(this.verifyLoading, item.Id, true);
      try {
        await this.$utils.callBackendPost(this, "/admin/updateReviewer", {
          ...item,
          NeedsVerification: newValue,
          Experience: JSON.stringify(this.parseExperience(item)),
        });
        item.NeedsVerification = newValue;
      } catch (error) {
        this.$set(this.verifyErrors, item.Id, error.message);
      } finally {
        this.$set(this.verifyLoading, item.Id, false);
      }
    },
    parseExperience(item) {
      if (!item || !item.Experience) {
        return [];
      }
      try {
        var parsed = JSON.parse(item.Experience);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },
    getReviewers() {
      this.reviewersLoaded = false;
      var dusi = this.$store.getters.getDusi;
      this.$http
        .post(
          this.$consts.BE_ADDRESS + "/admin/getReviewers",
          {
            userSearch: this.reviewerSearch,
            lastEmail: this.reviewersLastEmail,
            dusi: dusi,
          },
          {
            headers: {
              Authorization: this.$store.state.token,
            },
          },
        )
        .then((response) => {
          if (response.status === 200) {
            this.reviewers.push(...response.data);
            if (this.reviewers.length > 0) {
              this.reviewersLastEmail =
                this.reviewers[this.reviewers.length - 1].Email;
            } else {
              this.reviewersLastEmail = "";
            }
            return;
          }
          this.$utils.handleKnownRespCodes(this, response.status);
        })
        .catch((error) => {
          this.$utils.handleBackendError(this, error);
        })
        .finally(() => {
          this.reviewersLoaded = true;
        });
    },
  },
};
</script>
